import React, {
  useCallback,
  useState,
  useMemo,
  useContext,
  useEffect,
} from 'react';
import { useDropzone, FileWithPath } from 'react-dropzone';
import { enqueueSnackbar } from 'notistack';
import { IUploadDocument, PublicAPI } from '../../../api/PublicAPI';
import { GlobalContext } from '../../../providers/GlobalProvider';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { isTeamAdmin } from 'utils/teamAdmin';

// --- TODO  replace these with your actual implementation
const fileHandler = async (file: any) => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (
        file.type === 'text/csv' ||
        file.name.endsWith('.csv') ||
        file.name.endsWith('.xlsx')
      ) {
        resolve({
          name: file.name,
          size: file.size,
          type: file.type,
        });
      } else {
        console.error(
          'Mock fileHandler error: Unsupported file type:',
          file.type,
          file.name,
        );
        reject(
          new Error(
            'Mock fileHandler: Unsupported file type. Please upload CSV or XLSX.',
          ),
        );
      }
    }, 500);
  });
};

const exportToCSV = (filename: string, data: any, headers: any) => {
  const csvRows = [];
  csvRows.push(headers.join(','));
  data.forEach((item: any) => {
    const values = headers.map((header: any) => {
      const escaped = `${item[header]}`.replace(/"/g, '""');
      return `"${escaped}"`;
    });
    csvRows.push(values.join(','));
  });
  const csvString = csvRows.join('\r\n');
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    enqueueSnackbar(
      'Browser does not support link.download attribute for mock CSV export.',
      { variant: 'error' },
    );
  }
};

interface IBulkUploadModalProps {
  handleClose: () => void;
  isOpen: boolean;
}

interface TeamKeys {
  name: string;
  id: string;
}

const BulkUploadModal: React.FC<IBulkUploadModalProps> = ({
  handleClose,
  isOpen,
}) => {
  const [file, setFile] = useState<FileWithPath | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState<number>(1);
  const { cur_user } = useContext(GlobalContext);
  const [transactionID, setTransactionID] = useState<string>('');
  const [similarityThreshold, setSimilarityThreshold] = useState('0%');
  const [createTicket, setCreateTicket] = useState(false);
  const [ai_enabled, setAIEnabled] = useState(false);
  const [teamName, setTeamName] = useState<string>(
    cur_user.selected_team.name || '',
  );
  const [webhookURL, setWebhookURL] = useState<string>('');
  const [secret, setSecret] = useState<string>(cur_user.basics?.id || '');
  const [apiKey, setApiKey] = useState<string>('');
  const [teamsAPIKEYS, setTeamsAPIKEYS] = useState<TeamKeys[]>([]);
  const [document_id, setDocumentID] = useState<string>('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    if (cur_user.basics?.attributes?.api_keys) {
      try {
        const parsedObject = JSON.parse(cur_user.basics?.attributes?.api_keys);
        const arrayData: TeamKeys[] = Object.entries(parsedObject).map(
          ([name, id]) => ({
            name: name,
            id: id as string,
          }),
        );
        setTeamsAPIKEYS(arrayData);
      } catch (error) {
        console.error('Invalid JSON:', error);
      }
    }
  }, [cur_user.basics?.attributes?.api_keys]);

  const handleCancelPressed = () => {
    if (step === 1) {
      handleClose();
    } else if (step === 2) {
      setStep(1);
    }
  };

  const getTeamAPIKey = (teamNameLocal?: string) => {
    if (!teamNameLocal) {
      return '';
    }
    const team = teamsAPIKEYS.find(
      (t) => t.name === teamNameLocal?.toUpperCase(),
    );
    return team ? `${team.id}` : '';
  };

  useEffect(() => {
    if (isTeamAdmin(cur_user?.selected_team, cur_user.basics?.email)) {
      setApiKey(getTeamAPIKey(cur_user.selected_team?.name));
      setSecret(cur_user.basics?.id || '');
      setTeamName(cur_user.selected_team?.name || '');
    }
  }, [
    teamsAPIKEYS,
    cur_user.selected_team,
    cur_user.basics?.email,
    cur_user.selected_team?.name,
  ]);

  const templateHeaders = useMemo(
    () => ['name', 'bite_lens', 'address', 'country'],
    [],
  );

  const handleDeleteFile = useCallback(() => {
    setFile(undefined);
    enqueueSnackbar('File removed.', { variant: 'info' });
  }, [enqueueSnackbar]);

  const onDrop = useCallback(
    async (acceptedFiles: FileWithPath[]) => {
      if (isProcessing) {
        return;
      }
      const accptFile = acceptedFiles[0];
      if (!accptFile) {
        return;
      }

      setIsProcessing(true);
      setFile(accptFile);

      try {
        await fileHandler(accptFile);
        enqueueSnackbar('File ready for submission!', { variant: 'success' });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Error processing file. Please ensure it is a valid CSV or XLSX.';
        enqueueSnackbar(errorMessage, { variant: 'error' });
        setFile(undefined);
      } finally {
        setIsProcessing(false);
      }
    },
    [enqueueSnackbar, isProcessing],
  );

  const fileValidator = (currentFile: File) => {
    if (currentFile.size > 10 * 1024 * 1024) {
      // 20MB
      enqueueSnackbar('Your file is too large, upload a file less than 10MB', {
        variant: 'error',
      });
      return { code: 'size-too-large', message: 'File is larger than 10MB' };
    }
    const acceptedMimeTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ];
    const acceptedExtensions = ['.csv', '.xlsx'];

    const fileExtension = `.${currentFile.name
      .split('.')
      .pop()
      ?.toLowerCase()}`;

    if (
      !acceptedMimeTypes.includes(currentFile.type) &&
      !acceptedExtensions.includes(fileExtension)
    ) {
      enqueueSnackbar(
        'Invalid file type. Please upload a CSV or Excel (XLSX) file.',
        { variant: 'error' },
      );
      return {
        code: 'file-type-invalid',
        message: 'File type must be one of: .csv, .xlsx',
      };
    }
    return null;
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop: onDrop,
    validator: fileValidator,
    accept: ['.csv', '.xlsx'],
    multiple: false,
  });

  const handleDownloadTemplate = () => {
    exportToCSV('screening_template.csv', [], templateHeaders);
    enqueueSnackbar('Template download initiated.', { variant: 'info' });
  };

  const handleSubmit = async () => {
    if (step === 1) {
      if (!transactionID) {
        enqueueSnackbar('Please enter a transaction ID', { variant: 'error' });
        return;
      }

      if (!secret) {
        enqueueSnackbar('Please enter a secret', { variant: 'error' });
        return;
      }

      if (!apiKey) {
        enqueueSnackbar('Please enter an API key', { variant: 'error' });
        return;
      }

      if (!teamName) {
        enqueueSnackbar('Please select a team', { variant: 'error' });
        return;
      }

      setStep(2);
      return;
    }

    if (step === 2) {
      if (!file) {
        enqueueSnackbar('Please upload a file first', { variant: 'error' });
        return;
      }

      if (isProcessing) {
        enqueueSnackbar('File is currently being processed. Please wait.', {
          variant: 'warning',
        });
        return;
      }

      try {
        setIsProcessing(true);

        const uploadPayload: IUploadDocument = {
          file: file as unknown as File,
          transaction_id: transactionID,
          ai_enabled: ai_enabled,
          create_ticket: createTicket,
          similarity_threshold: similarityThreshold,
        };

        const headers = {
          ...(webhookURL && { webhook_url: webhookURL }),
          secret: secret,
          api_key: apiKey,
          team_name: teamName,
        };

        const response = await PublicAPI.upload_document(
          uploadPayload,
          headers,
        );

        if (response?.success) {
          enqueueSnackbar(`Success: ${response.message}`, {
            variant: 'success',
          });
          handleClose();
        } else {
          console.error('Upload failed response:', response);
          enqueueSnackbar(`File upload failed. ${response.message}.`, {
            variant: 'error',
          });
        }
      } catch (error) {
        console.error('Error during file upload:', error);
        enqueueSnackbar('Unexpected error during file upload', {
          variant: 'error',
        });
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const dropzoneClassName = useMemo(() => {
    const baseClass = 'dropzone-base';
    if (isDragAccept) {
      return `${baseClass} dropzone-accept`;
    }
    if (isDragReject) {
      return `${baseClass} dropzone-reject`;
    }
    if (isDragActive) {
      return `${baseClass} dropzone-active`;
    }
    return file
      ? `${baseClass} dropzone-file-present`
      : `${baseClass} dropzone-default`;
  }, [isDragActive, isDragAccept, isDragReject, file]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className='modal-backdrop bulk-upload-modal'>
      <div className='modal-container'>
        <div className='modal-header'>
          {step === 1 ? (
            <p className='modal-description'>
              Please enter the details below:{' '}
            </p>
          ) : (
            <p className='modal-description'>
              Upload a CSV or Excel (XLSX) file to perform bulk screening. You
              can download a template as an example.
            </p>
          )}
          <button onClick={handleClose} className='modal-close-button'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M18 6L6 18M6 6L18 18' />
            </svg>
          </button>
        </div>

        <div className='modal-body'>
          {step === 1 ? (
            <div className='modal-grid' style={{ gridTemplateColumns: '1fr' }}>
              <div className='screening-input-field-container'>
                <input
                  id='transactionID' // Unique ID
                  type='text'
                  placeholder='Required: Enter Transaction ID'
                  className='screening-input-field-content'
                  name='transactionID'
                  value={transactionID}
                  onChange={(e) => setTransactionID(e.target.value)}
                  required
                />
              </div>
              <div className='screening-input-field-container'>
                <input
                  id='webhookURL' // Unique ID
                  type='text'
                  placeholder='Optional: Enter Webhook URL'
                  className='screening-input-field-content'
                  name='WebhookURL'
                  value={webhookURL}
                  onChange={(e) => setWebhookURL(e.target.value)}
                  required
                />
              </div>
              <div className='screening-input-field-container'>
                <input
                  id='secretKey' // Unique ID
                  type={showSecret ? 'text' : 'password'}
                  placeholder='Required: Enter Secret Key'
                  className='screening-input-field-content'
                  name='secret'
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  required
                />
                <span
                  onClick={() => setShowSecret(!showSecret)}
                  style={{
                    cursor: 'pointer',
                    color: '#888',
                  }}
                >
                  {showSecret ? <VisibilityOff /> : <Visibility />}
                </span>
              </div>
              <div className='screening-input-field-container'>
                <input
                  id='apiKeyVal'
                  type={showApiKey ? 'text' : 'password'}
                  placeholder='Required: Enter API Key'
                  className='screening-input-field-content'
                  name='apiKey'
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  required
                />
                <span
                  onClick={() => setShowApiKey(!showApiKey)}
                  style={{
                    cursor: 'pointer',
                    color: '#888',
                  }}
                >
                  {showApiKey ? <VisibilityOff /> : <Visibility />}
                </span>
              </div>
              <div className='screening-input-field-container'>
                <input
                  id='documentId'
                  type='text'
                  placeholder='Optional: Enter Document Id'
                  className='screening-input-field-content'
                  name='documentID'
                  value={document_id}
                  onChange={(e) => setDocumentID(e.target.value)}
                />
              </div>
              <div className='screening-input-field-container'>
                <input
                  id='similarityThreshold'
                  type='text'
                  placeholder='Optional: Enter Similarity Threshold as a percentage'
                  className='screening-input-field-content'
                  name='similarityThreshold'
                  value={similarityThreshold}
                  onChange={(e) => setSimilarityThreshold(e.target.value)}
                />
              </div>
              <div className='screening-input-field-container'>
                <input
                  id='teamName'
                  type='text'
                  placeholder='Required: Enter Team Name'
                  className='screening-input-field-content'
                  name='teamName'
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '24px',
                  marginTop: '10px',
                  justifyContent: 'space-evenly',
                }}
              >
                <div
                  className='screening-checkbox-wrapper'
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '8px',
                  }}
                >
                  <input
                    id='aiEnabledCheckbox'
                    type='checkbox'
                    className='bulk-upload-checkbox'
                    name='ai_enabled'
                    checked={ai_enabled}
                    onChange={(e) => setAIEnabled(e.target.checked)}
                  />
                  <label
                    htmlFor='aiEnabledCheckbox'
                    className='screening-checkbox-label'
                  >
                    AI Enabled
                  </label>
                </div>

                <div
                  className='screening-checkbox-wrapper'
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '8px',
                  }}
                >
                  <input
                    id='createTicketCheckbox'
                    type='checkbox'
                    className='bulk-upload-checkbox'
                    name='create_ticket'
                    checked={createTicket}
                    onChange={(e) => setCreateTicket(e.target.checked)}
                  />
                  <label
                    htmlFor='createTicketCheckbox'
                    className='screening-checkbox-label'
                  >
                    Save
                  </label>
                </div>
              </div>
            </div>
          ) : (
            <div className='modal-grid'>
              <div className='modal-section'>
                <h3 className='modal-section-title'>1. Download Template</h3>
                <button
                  onClick={handleDownloadTemplate}
                  className='button button-primary button-full-width'
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width='19'
                    height='18'
                    viewBox='0 0 19 18'
                    fill='none'
                    className='button-icon'
                  >
                    <path
                      d='M6.5 9L9.5 12M9.5 12L12.5 9M9.5 12V6M17 9C17 13.1421 13.6421 16.5 9.5 16.5C5.35786 16.5 2 13.1421 2 9C2 4.85786 5.35786 1.5 9.5 1.5C13.6421 1.5 17 4.85786 17 9Z'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                  Download Template
                </button>
                <p className='modal-section-subtitle'>
                  Download a .csv template file with example headers.
                </p>
              </div>
              <div className='modal-section'>
                <h3 className='modal-section-title'>2. Upload File</h3>
                <div {...getRootProps()} className={dropzoneClassName}>
                  <input {...getInputProps()} />
                  {file ? (
                    <div className='dropzone-content-item'>
                      <svg
                        className='dropzone-icon-success'
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                        strokeWidth={1.5}
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'
                        />
                      </svg>
                      <p className='dropzone-filename'>{file.name}</p>
                      <p className='dropzone-filesize'>
                        ({(file.size / 1024).toFixed(2)} KB)
                      </p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFile();
                        }}
                        className='dropzone-remove-button'
                      >
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='16'
                          height='16'
                          viewBox='0 0 20 20'
                          fill='none'
                          stroke='currentColor'
                          strokeWidth='1.66667'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          className='button-icon'
                        >
                          <path d='M7.5 2.5H12.5M2.5 5H17.5M15.8333 5L15.2489 13.7661C15.1612 15.0813 15.1174 15.7389 14.8333 16.2375C14.5833 16.6765 14.206 17.0294 13.7514 17.2497C13.235 17.5 12.5759 17.5 11.2578 17.5H8.74221C7.42409 17.5 6.76503 17.5 6.24861 17.2497C5.79396 17.0294 5.41674 16.6765 5.16665 16.2375C4.88259 15.7389 4.83875 15.0813 4.75107 13.7661L4.16667 5M8.33333 8.75V12.9167M11.6667 8.75V12.9167' />
                        </svg>
                        Remove File
                      </button>
                    </div>
                  ) : isProcessing ? (
                    <div className='dropzone-content-item'>
                      <svg
                        className='dropzone-icon-spinner'
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                      >
                        <circle
                          className='opacity-25'
                          cx='12'
                          cy='12'
                          r='10'
                          stroke='currentColor'
                          strokeWidth='4'
                        ></circle>
                        <path
                          className='opacity-75'
                          fill='currentColor'
                          d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                        ></path>
                      </svg>
                      <p className='dropzone-text'>Processing file...</p>
                    </div>
                  ) : (
                    <div className='dropzone-content-item'>
                      <svg
                        className='dropzone-icon-default'
                        xmlns='http://www.w3.org/2000/svg'
                        fill='none'
                        viewBox='0 0 24 24'
                        strokeWidth={1.5}
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          d='M12 16.5V9.75m0 0l-3 3m3-3 3 3M6.75 19.5a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.338-2.32 5.75 5.75 0 0 1 1.136 11.097m0 0H5.25'
                        />
                      </svg>
                      <p className='dropzone-text'>
                        <span className='dropzone-text-highlight'>
                          Click to upload
                        </span>{' '}
                        or drag and drop
                      </p>
                      <p className='dropzone-subtext'>CSV or XLSX (max 10MB)</p>
                      {isDragReject && (
                        <p className='dropzone-error-text'>
                          Invalid file type or size.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className='wizard-btn-group'>
          <div className='wizard-btn-container'>
            <div
              className='bulk-upload-modal-back-btn'
              onClick={handleCancelPressed}
            >
              <div className='wizard-next-btn-text'>
                {step === 1 ? 'Cancel' : 'Back'}
              </div>
            </div>
            <div
              className={`wizard-primary-btn${
                !file ? (step === 2 ? '-disabled' : '') : ''
              }`}
              onClick={handleSubmit}
            >
              <div className='wizard-next-btn-text'>
                {step === 1
                  ? 'Next'
                  : isProcessing
                  ? 'Processing...'
                  : 'Submit'}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
